/**
 * Connected Banks List Component
 *
 * Displays all connected bank accounts for a tenant with multi-bank support.
 * Shows a grid of bank cards with status, test connection, and disconnect actions.
 *
 * @fileoverview Multi-bank connection management UI
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Icon,
  Button,
  useToast,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Spinner,
} from '@chakra-ui/react';
import {
  MdAccountBalance,
  MdCheckCircle,
  MdWarning,
  MdCancel,
  MdAdd,
  MdWifi,
  MdDelete,
  MdRefresh,
  MdSync,
  MdSchedule,
} from 'react-icons/md';
import { useUser, PlaidTokenMetadata } from '../../Context/userContext';
import { supabase } from '../../lib/supabase';
import { apiFetch, apiJson } from '../../lib/api';
import { TestConnectionResult } from './TestConnectionLogWindow';
import { PlaidLinkOnSuccess, PlaidLinkOnExit } from 'react-plaid-link';
import { usePlaidLinkWithLogging } from '../../hooks/usePlaidLinkWithLogging';
import { toUserMessage } from '../../lib/apiError';

/**
 * Get status badge configuration
 */
function getStatusConfig(status: PlaidTokenMetadata['status']): {
  colorScheme: string;
  icon: React.ElementType;
  label: string;
} {
  switch (status) {
    case 'ACTIVE':
      return {
        colorScheme: 'green',
        icon: MdCheckCircle,
        label: 'Active',
      };
    case 'LOGIN_REQUIRED':
      return {
        colorScheme: 'yellow',
        icon: MdWarning,
        label: 'Login Required',
      };
    case 'REVOKED':
      return {
        colorScheme: 'red',
        icon: MdCancel,
        label: 'Revoked',
      };
    default:
      return {
        colorScheme: 'gray',
        icon: MdWarning,
        label: 'Unknown',
      };
  }
}

/**
 * Format date to readable string
 */
function formatDate(dateString?: string | Date): string {
  if (!dateString) return 'Unknown';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Unknown';
  }
}

// ============================================================================
// Bank Card Component
// ============================================================================

interface BankCardProps {
  token: PlaidTokenMetadata;
  onTestResult?: (tokenId: string, result: TestConnectionResult) => void;
  onDisconnect: (tokenId: string) => void;
  onReconnect: (tokenId: string) => void;
}

const BankCard: React.FC<BankCardProps> = ({
  token,
  onTestResult,
  onDisconnect,
  onReconnect,
}) => {
  const { selectedTenant } = useUser();
  const [isTesting, setIsTesting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const toast = useToast();
  const statusConfig = getStatusConfig(token.status);
  const disconnectModal = useDisclosure();

  const handleTestConnection = useCallback(async () => {
    if (!selectedTenant) return;

    setIsTesting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const result = await apiJson<TestConnectionResult>(
        `/api/plaid-token/${token.id}/test`,
        {
          headers: {
            'x-tenant-id': selectedTenant.id,
          },
        },
        session.access_token
      );

      onTestResult?.(token.id, result);

      if (result.success) {
        toast({
          title: 'Connection Successful',
          description: `Found ${result.accountCount} account(s) at ${token.institutionName || 'your bank'}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Connection Failed',
          description: result.error || 'Unable to verify connection',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      const errorMessage = toUserMessage(error, 'Please try again in a moment.');
      toast({
        title: 'Test Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsTesting(false);
    }
  }, [selectedTenant, token.id, token.institutionName, onTestResult, toast]);

  const handleResetConnection = useCallback(async () => {
    if (!selectedTenant) return;

    setIsResetting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Trigger a full sync to reset and re-fetch all transactions. When
      // Redis is configured this is handed off to a queue and only just
      // started — the toast copy below reflects that instead of implying
      // the resync already finished (see useTransactionSync's polling for
      // the pattern that actually waits on the result).
      const response = await apiJson<
        { success: boolean } | { status: 'queued'; jobId: string }
      >(
        '/api/plaid-transactions/sync',
        {
          method: 'POST',
          headers: {
            'x-tenant-id': selectedTenant.id,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ forceFullSync: true }),
        },
        session.access_token
      );

      const isQueued = 'status' in response && response.status === 'queued';

      toast({
        title: isQueued ? 'Reset Queued' : 'Reset Complete',
        description: isQueued
          ? `Resync for ${token.institutionName || 'bank'} has started in the background and may take a few minutes.`
          : `Transactions for ${token.institutionName || 'bank'} have been reset and re-synced.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const errorMessage = toUserMessage(error, 'Please try again in a moment.');
      toast({
        title: 'Reset Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResetting(false);
    }
  }, [selectedTenant, token.institutionName, toast]);

  const handleDisconnect = useCallback(async () => {
    if (!selectedTenant) return;

    setIsDisconnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      await apiFetch(
        `/api/plaid-token/${token.id}`,
        {
          method: 'DELETE',
          headers: {
            'x-tenant-id': selectedTenant.id,
          },
        },
        session.access_token
      );

      toast({
        title: 'Bank Disconnected',
        description: `${token.institutionName || 'Bank'} has been disconnected.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onDisconnect(token.id);
      disconnectModal.onClose();
    } catch (error) {
      const errorMessage = toUserMessage(error, 'Please try again in a moment.');
      toast({
        title: 'Disconnect Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDisconnecting(false);
    }
  }, [selectedTenant, token.id, token.institutionName, onDisconnect, toast, disconnectModal]);

  const needsReconnect = token.status === 'LOGIN_REQUIRED' || token.status === 'REVOKED';
  const expirationImminent = token.status === 'ACTIVE' && !!token.isExpirationImminent;

  return (
    <>
      <Card
        bg="bg.surface"
        borderColor={needsReconnect ? 'yellow.500' : expirationImminent ? 'orange.500' : 'xacto.mutedNavy'}
        borderWidth="1px"
        _hover={{ borderColor: 'accent.primary' }}
        transition="border-color 0.2s"
      >
        <CardBody>
          <VStack align="stretch" spacing={3}>
            {/* Header with bank name and status */}
            <HStack justify="space-between" align="start">
              <HStack spacing={3}>
                <Box
                  bg="bg.muted"
                  p={2}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={MdAccountBalance} color="accent.primary" boxSize={5} />
                </Box>
                <VStack align="start" spacing={0}>
                  <Text color="text.primary" fontWeight="semibold" fontSize="sm">
                    {token.institutionName ?? 'Unknown Bank'}
                  </Text>
                  <Text color="text.muted" fontSize="xs">
                    Connected {formatDate(token.createdAt)}
                  </Text>
                </VStack>
              </HStack>
              <Badge
                colorScheme={statusConfig.colorScheme}
                display="flex"
                alignItems="center"
                gap={1}
                px={2}
                py={0.5}
                fontSize="xs"
              >
                <Icon as={statusConfig.icon} boxSize={3} />
                {statusConfig.label}
              </Badge>
            </HStack>

            {/* Warning for login required */}
            {token.status === 'LOGIN_REQUIRED' && (
              <Alert status="warning" bg="yellow.900" borderRadius="md" py={2}>
                <AlertIcon color="yellow.400" boxSize={4} />
                <AlertDescription color="yellow.100" fontSize="xs">
                  {token.errorCode === 'INVALID_ACCESS_TOKEN'
                    ? 'Connection invalid. Please reconnect.'
                    : 'Re-authentication required.'}
                </AlertDescription>
              </Alert>
            )}

            {/* Warning for imminent consent expiration */}
            {token.status === 'ACTIVE' && token.isExpirationImminent && (
              <Alert status="warning" bg="orange.900" borderRadius="md" py={2}>
                <AlertIcon as={MdSchedule} color="orange.300" boxSize={4} />
                <AlertDescription color="orange.100" fontSize="xs">
                  Bank authorization expires
                  {token.daysUntilExpiration != null && token.daysUntilExpiration > 0
                    ? ` in ${token.daysUntilExpiration} day${token.daysUntilExpiration === 1 ? '' : 's'}`
                    : ' today'}
                  . Reconnect now to avoid disconnection.
                </AlertDescription>
              </Alert>
            )}

            {/* Action buttons - vertical layout for better text visibility */}
            <VStack spacing={2} pt={1} align="stretch">
              {/* Test Connection */}
              <Button
                leftIcon={<Icon as={MdWifi} />}
                variant="outline"
                colorScheme="cyan"
                borderColor="xacto.electricCyan"
                color="accent.primary"
                size="sm"
                w="full"
                justifyContent="flex-start"
                onClick={handleTestConnection}
                isLoading={isTesting}
                loadingText="Testing..."
                isDisabled={needsReconnect}
                _hover={{
                  bg: 'xacto.mutedNavy',
                  borderColor: 'cyan.400',
                }}
              >
                Test Connection
              </Button>

              {/* Reset Connection */}
              <Button
                leftIcon={<Icon as={MdSync} />}
                variant="outline"
                colorScheme="orange"
                size="sm"
                w="full"
                justifyContent="flex-start"
                onClick={handleResetConnection}
                isLoading={isResetting}
                loadingText="Resetting..."
                isDisabled={needsReconnect}
                _hover={{
                  bg: 'orange.900',
                }}
              >
                Reset Connection
              </Button>

              {/* Reconnect */}
              <Button
                leftIcon={<Icon as={MdRefresh} />}
                colorScheme={needsReconnect ? 'yellow' : 'gray'}
                variant={needsReconnect ? 'solid' : 'outline'}
                size="sm"
                w="full"
                justifyContent="flex-start"
                onClick={() => onReconnect(token.id)}
              >
                Reconnect
              </Button>

              {/* Remove button - less prominent */}
              <Button
                leftIcon={<Icon as={MdDelete} />}
                variant="ghost"
                colorScheme="red"
                size="xs"
                w="full"
                justifyContent="flex-start"
                onClick={disconnectModal.onOpen}
                _hover={{ bg: 'red.900' }}
              >
                Remove Bank
              </Button>
            </VStack>

          </VStack>
        </CardBody>
      </Card>

      {/* Disconnect Confirmation Modal */}
      <Modal isOpen={disconnectModal.isOpen} onClose={disconnectModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="bg.surface" borderColor="xacto.mutedNavy">
          <ModalHeader color="text.primary">Disconnect Bank</ModalHeader>
          <ModalCloseButton color="text.secondary" />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="text.secondary">
                Are you sure you want to disconnect <strong>{token.institutionName || 'this bank'}</strong>?
              </Text>
              <Alert status="warning" bg="yellow.900" borderRadius="md">
                <AlertIcon color="yellow.400" />
                <AlertDescription color="yellow.100" fontSize="sm">
                  This will remove all transaction data from this bank. This action cannot be undone.
                </AlertDescription>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={disconnectModal.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDisconnect}
              isLoading={isDisconnecting}
              loadingText="Disconnecting..."
            >
              Disconnect
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// ============================================================================
// Add Bank Button Component
// ============================================================================

// localStorage keys — must survive App2App browser context switches (Chase mobile).
const ADD_BANK_OAUTH_KEY = 'xacto_plaid_add_bank_oauth';
// Unified pending-link state read by /plaid/oauth-return.
const OAUTH_PENDING_KEY = 'xacto_plaid_oauth_pending';

interface AddBankButtonProps {
  onSuccess: () => Promise<void> | void;
}

const AddBankButton: React.FC<AddBankButtonProps> = ({ onSuccess }) => {
  const { selectedTenant } = useUser();
  const isExchangingRef = React.useRef(false);
  const toast = useToast();

  // On OAuth return the page reloads and React state is gone. Restore the
  // link token from localStorage so usePlaidLink can resume the flow.
  const isOAuthReturn = window.location.href.includes('?oauth_state_id=');
  const [linkToken, setLinkTokenState] = useState<string | null>(() => {
    const stored = isOAuthReturn ? localStorage.getItem(ADD_BANK_OAUTH_KEY) : null;
    console.log('[AddBankButton] INIT', {
      isOAuthReturn,
      url: window.location.href,
      storedTokenPrefix: stored?.slice(0, 30) ?? null,
      allPlaidKeys: Object.keys(localStorage).filter(k => k.startsWith('xacto_plaid')),
    });
    return stored;
  });
  // Must be window.location.href raw — no encoding, no extra params.
  const receivedRedirectUri = isOAuthReturn ? window.location.href : undefined;
  const [isCreatingToken, setIsCreatingToken] = useState(false);

  const setLinkToken = useCallback((token: string | null) => {
    setLinkTokenState(token);
    if (token) {
      localStorage.setItem(ADD_BANK_OAUTH_KEY, token);
    } else {
      localStorage.removeItem(ADD_BANK_OAUTH_KEY);
      localStorage.removeItem(OAUTH_PENDING_KEY);
    }
  }, []);

  const createLinkToken = useCallback(async () => {
    if (!selectedTenant) return;

    setIsCreatingToken(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const data = await apiJson<{ linkToken: string }>(
        '/api/signup/create-link-token',
        {
          method: 'POST',
          headers: {
            'x-tenant-id': selectedTenant.id,
          },
          body: JSON.stringify({
            tenantId: selectedTenant.id,
          }),
        },
        session.access_token
      );

      // Save before opening Link so the state survives the OAuth bank redirect.
      const pendingState = { linkToken: data.linkToken, mode: 'add_bank', tenantId: selectedTenant.id };
      localStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(pendingState));
      console.log('[AddBankButton] link token created, saved to localStorage', {
        tenantId: selectedTenant.id,
        linkTokenPrefix: data.linkToken?.slice(0, 30),
        pendingState,
      });
      setLinkToken(data.linkToken);
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(error, "We couldn't start the bank connection. Please try again."),
        status: 'error',
        duration: 5000,
      });
      setIsCreatingToken(false);
    }
  }, [selectedTenant, toast, setLinkToken]);

  const handlePlaidSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      if (!selectedTenant) return;
      if (isExchangingRef.current) return;
      isExchangingRef.current = true;

      console.log('[PlaidConnect] onSuccess received', {
        mode: 'add_bank',
        tenantId: selectedTenant.id,
        institutionId: metadata.institution?.institution_id ?? null,
        institutionName: metadata.institution?.name ?? null,
        accountCount: metadata.accounts?.length ?? 0,
        linkSessionId: metadata.link_session_id,
      });

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Not authenticated');
        }

        const response = await apiFetch('/api/signup/exchange-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            publicToken,
            tenantId: selectedTenant.id,
            institutionId: metadata.institution?.institution_id,
            institutionName: metadata.institution?.name,
            linkSessionId: metadata.link_session_id,
          }),
        }, session.access_token);

        console.log('[PlaidConnect] exchange-token response', {
          ok: response.ok,
          status: response.status,
          mode: 'add_bank',
          tenantId: selectedTenant.id,
          institutionId: metadata.institution?.institution_id ?? null,
          linkSessionId: metadata.link_session_id,
        });

        if (!response.ok) {
          throw new Error('Failed to exchange token');
        }

        toast({
          title: 'Success',
          description: `${metadata.institution?.name || 'Bank'} connected successfully.`,
          status: 'success',
          duration: 3000,
        });

        localStorage.removeItem(OAUTH_PENDING_KEY);
        setLinkToken(null);
        setIsCreatingToken(false);
        await onSuccess();
      } catch (error) {
        console.error('[PlaidConnect] exchange-token failed', {
          mode: 'add_bank',
          tenantId: selectedTenant.id,
          institutionId: metadata.institution?.institution_id ?? null,
          linkSessionId: metadata.link_session_id,
          error: error instanceof Error ? error.message : String(error),
        });
        toast({
          title: 'Something went wrong',
          description: toUserMessage(error, "We couldn't connect the account. Please try again."),
          status: 'error',
          duration: 5000,
        });
      }
    },
    [selectedTenant, toast, onSuccess, setLinkToken]
  );

  const handlePlaidExit: PlaidLinkOnExit = useCallback((err, metadata) => {
    if (err) {
      console.error('[PlaidConnect] onExit', {
        mode: 'add_bank',
        tenantId: selectedTenant?.id ?? null,
        error_type: err.error_type,
        error_code: err.error_code,
        error_message: err.error_message,
        request_id: metadata?.request_id,
        link_session_id: metadata?.link_session_id,
      });
      if (err.error_code === 'INVALID_LINK_TOKEN') {
        localStorage.removeItem(ADD_BANK_OAUTH_KEY);
        localStorage.removeItem(OAUTH_PENDING_KEY);
        toast({
          title: 'Session expired',
          description: 'The bank connection session expired. Please try again.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
    localStorage.removeItem(OAUTH_PENDING_KEY);
    setLinkToken(null);
    setIsCreatingToken(false);
  }, [setLinkToken, toast]);

  const { open, ready } = usePlaidLinkWithLogging({
    mode: 'add_bank',
    tenantId: selectedTenant?.id ?? '',
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
    ...(receivedRedirectUri && { receivedRedirectUri }),
  });

  // Auto-open Plaid Link when token is ready
  React.useEffect(() => {
    console.log('[AddBankButton] usePlaidLink state', {
      ready,
      hasToken: !!linkToken,
      linkTokenPrefix: linkToken?.slice(0, 30),
      isOAuthReturn,
      receivedRedirectUri,
    });
    if (linkToken && ready) {
      console.log('[AddBankButton] opening Plaid Link');
      open();
    }
  }, [linkToken, ready, open]);

  return (
    <Button
      leftIcon={isCreatingToken ? <Spinner size="sm" /> : <Icon as={MdAdd} />}
      colorScheme="cyan"
      variant="solid"
      bg="xacto.electricCyan"
      color="xacto.deepNavy"
      _hover={{ bg: 'cyan.400' }}
      size="md"
      onClick={createLinkToken}
      isDisabled={isCreatingToken || !selectedTenant}
    >
      {isCreatingToken ? 'Connecting...' : 'Add Bank'}
    </Button>
  );
};

// ============================================================================
// Reconnect Plaid Link Component
// ============================================================================

const RECONNECT_LINK_TOKEN_KEY = 'xacto_plaid_reconnect_link_token';
const RECONNECT_TOKEN_ID_KEY = 'xacto_plaid_reconnect_token_id';

interface ReconnectPlaidLinkProps {
  tokenId: string;
  onSuccess: () => void;
  onExit: () => void;
}

const ReconnectPlaidLink: React.FC<ReconnectPlaidLinkProps> = ({
  tokenId,
  onSuccess,
  onExit,
}) => {
  const { selectedTenant } = useUser();
  const isExchangingRef = React.useRef(false);
  const toast = useToast();

  const isOAuthReturn = window.location.href.includes('?oauth_state_id=');

  // On OAuth return, restore the link token from localStorage instead of
  // fetching a new one (which would be a different token and break the flow).
  const [linkToken, setLinkTokenState] = useState<string | null>(() => {
    const stored = isOAuthReturn ? localStorage.getItem(RECONNECT_LINK_TOKEN_KEY) : null;
    console.log('[ReconnectPlaidLink] INIT', {
      tokenId,
      isOAuthReturn,
      url: window.location.href,
      storedTokenPrefix: stored?.slice(0, 30) ?? null,
      allPlaidKeys: Object.keys(localStorage).filter(k => k.startsWith('xacto_plaid')),
    });
    return stored;
  });
  // Must be window.location.href raw — no encoding, no extra params.
  const receivedRedirectUri = isOAuthReturn ? window.location.href : undefined;

  const setLinkToken = useCallback((token: string | null) => {
    setLinkTokenState(token);
    if (token) {
      localStorage.setItem(RECONNECT_LINK_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(RECONNECT_LINK_TOKEN_KEY);
      localStorage.removeItem(RECONNECT_TOKEN_ID_KEY);
      localStorage.removeItem(OAUTH_PENDING_KEY);
    }
  }, []);

  // Fetch update mode link token on mount — skip on OAuth return since
  // the token was already created and is stored in sessionStorage.
  React.useEffect(() => {
    if (isOAuthReturn) return;

    const abortController = new AbortController();

    const fetchUpdateLinkToken = async () => {
      if (!selectedTenant) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (abortController.signal.aborted) return;
        if (!session?.access_token) {
          throw new Error('Not authenticated');
        }

        const data = await apiJson<{ link_token: string }>(
          '/api/link-token-update',
          {
            method: 'POST',
            headers: {
              'x-tenant-id': selectedTenant.id,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ tokenId }),
            signal: abortController.signal,
          },
          session.access_token
        );

        if (!abortController.signal.aborted) {
          // Save before opening Link so the state survives the OAuth bank redirect.
          const pendingState = { linkToken: data.link_token, mode: 'reconnect', tokenId, tenantId: selectedTenant!.id };
          localStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(pendingState));
          console.log('[ReconnectPlaidLink] update link token fetched, saved to localStorage', {
            tokenId,
            tenantId: selectedTenant!.id,
            linkTokenPrefix: data.link_token?.slice(0, 30),
            pendingState,
          });
          setLinkToken(data.link_token);
        }
      } catch (error) {
        if (abortController.signal.aborted) return;
        const errorMessage = toUserMessage(error, 'Please try again in a moment.');
        toast({
          title: 'Reconnect Failed',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        onExit();
      }
    };

    fetchUpdateLinkToken();
    return () => abortController.abort();
  }, [selectedTenant, tokenId, toast, onExit, isOAuthReturn, setLinkToken]);

  const handleSuccess: PlaidLinkOnSuccess = useCallback(async (publicToken, metadata) => {
    if (isExchangingRef.current) return;
    isExchangingRef.current = true;
    console.log('[ReconnectPlaidLink] onSuccess RECEIVED', {
      mode: 'reconnect',
      tokenId,
      institution: metadata.institution?.name,
      institutionId: metadata.institution?.institution_id,
      tenantId: selectedTenant?.id,
      linkSessionId: metadata.link_session_id,
    });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await apiFetch(
        '/api/signup/exchange-token',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            publicToken,
            tenantId: selectedTenant!.id,
            institutionId: metadata.institution?.institution_id,
            institutionName: metadata.institution?.name,
            linkSessionId: metadata.link_session_id,
          }),
        },
        session.access_token
      );
      if (!response.ok) throw new Error('Failed to exchange token on reconnect');

      toast({
        title: 'Reconnected Successfully',
        description: `${metadata.institution?.name || 'Bank'} has been reconnected.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      localStorage.removeItem(OAUTH_PENDING_KEY);
      setLinkToken(null);
      onSuccess();
    } catch (error) {
      const errorMessage = toUserMessage(error, 'Please try again in a moment.');
      toast({
        title: 'Reconnect Error',
        description: `Bank reconnected but status update failed: ${errorMessage}`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      localStorage.removeItem(OAUTH_PENDING_KEY);
      setLinkToken(null);
      onSuccess(); // Still refresh since Plaid side was successful
    }
  }, [toast, onSuccess, tokenId, selectedTenant, setLinkToken]);

  const { open, ready } = usePlaidLinkWithLogging({
    mode: 'reconnect',
    tenantId: selectedTenant?.id ?? '',
    token: linkToken,
    onSuccess: handleSuccess,
    onExit: (err, metadata) => {
      if (err?.error_code === 'INVALID_LINK_TOKEN') {
        localStorage.removeItem(RECONNECT_LINK_TOKEN_KEY);
        localStorage.removeItem(RECONNECT_TOKEN_ID_KEY);
        localStorage.removeItem(OAUTH_PENDING_KEY);
        toast({
          title: 'Session expired',
          description: 'The reconnection session expired. Please try again.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
      if (err) {
        console.error('[PlaidReconnect] onExit', {
          mode: 'reconnect',
          tenantId: selectedTenant?.id ?? null,
          error_type: err.error_type,
          error_code: err.error_code,
          error_message: err.error_message,
          request_id: metadata?.request_id,
          link_session_id: metadata?.link_session_id,
        });
      }
      localStorage.removeItem(OAUTH_PENDING_KEY);
      setLinkToken(null);
      onExit();
    },
    ...(receivedRedirectUri && { receivedRedirectUri }),
  });

  // Auto-open Plaid Link when ready
  React.useEffect(() => {
    console.log('[ReconnectPlaidLink] usePlaidLink state', {
      ready,
      hasToken: !!linkToken,
      linkTokenPrefix: linkToken?.slice(0, 30),
      isOAuthReturn,
      receivedRedirectUri,
      tokenId,
    });
    if (linkToken && ready) {
      console.log('[ReconnectPlaidLink] opening Plaid Link', { tokenId, isOAuthReturn });
      open();
    }
  }, [linkToken, ready, open]);

  // Render nothing - Plaid Link opens as a modal
  return null;
};

// ============================================================================
// Loading Skeleton
// ============================================================================

const BankCardSkeleton: React.FC = () => (
  <Card bg="bg.surface" borderColor="xacto.mutedNavy" borderWidth="1px">
    <CardBody>
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between">
          <HStack spacing={3}>
            <Skeleton boxSize={10} borderRadius="md" />
            <VStack align="start" spacing={1}>
              <Skeleton height="14px" width="120px" />
              <Skeleton height="12px" width="80px" />
            </VStack>
          </HStack>
          <Skeleton height="20px" width="60px" borderRadius="full" />
        </HStack>
        <HStack spacing={2}>
          <Skeleton height="32px" flex={1} borderRadius="md" />
          <Skeleton height="32px" width="80px" borderRadius="md" />
        </HStack>
      </VStack>
    </CardBody>
  </Card>
);

// ============================================================================
// Main Component
// ============================================================================

interface ConnectedBanksListProps {
  onTestResult?: (tokenId: string, result: TestConnectionResult) => void;
}

export const ConnectedBanksList: React.FC<ConnectedBanksListProps> = ({
  onTestResult,
}) => {
  const { plaidTokens, selectedTenant, isLoading, fetchPlaidTokenStatus, refreshAll } = useUser();

  // On OAuth return (Wells Fargo, Chase, etc.) the page reloads and all React
  // state is lost. Restore reconnectingTokenId from localStorage so that
  // ReconnectPlaidLink mounts and can resume the OAuth continuation.
  const [reconnectingTokenId, setReconnectingTokenId] = useState<string | null>(() => {
    if (window.location.href.includes('?oauth_state_id=')) {
      return localStorage.getItem(RECONNECT_TOKEN_ID_KEY);
    }
    return null;
  });

  const handleDisconnect = useCallback(() => {
    // Refresh the token list after disconnect
    refreshAll();
  }, [refreshAll]);

  const handleAddSuccess = useCallback(() => {
    // Refresh token list immediately, then re-check shortly after to catch delayed status propagation
    const refresh = async () => {
      await refreshAll();

      setTimeout(() => {
        fetchPlaidTokenStatus().catch((err) => {
          console.warn('[PlaidConnect] delayed status refresh failed', err);
        });
      }, 1200);
    };

    return refresh();
  }, [refreshAll, fetchPlaidTokenStatus]);

  const handleReconnect = useCallback((tokenId: string) => {
    localStorage.setItem(RECONNECT_TOKEN_ID_KEY, tokenId);
    setReconnectingTokenId(tokenId);
  }, []);

  const handleReconnectSuccess = useCallback(() => {
    setReconnectingTokenId(null);
    localStorage.removeItem(RECONNECT_TOKEN_ID_KEY);
    refreshAll();
  }, [refreshAll]);

  const handleReconnectExit = useCallback(() => {
    setReconnectingTokenId(null);
    localStorage.removeItem(RECONNECT_TOKEN_ID_KEY);
  }, []);

  // Skip the loading skeleton when an OAuth session is in progress — the
  // Plaid Link handlers need to mount immediately to resume the flow.
  const hasActiveOAuthSession =
    window.location.href.includes('?oauth_state_id=') &&
    (!!localStorage.getItem(ADD_BANK_OAUTH_KEY) ||
      !!localStorage.getItem(RECONNECT_LINK_TOKEN_KEY));

  // Show skeleton while loading
  if (isLoading && !hasActiveOAuthSession) {
    return (
      <Card bg="bg.surface" borderColor="xacto.mutedNavy" borderWidth="1px">
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <Skeleton height="24px" width="180px" />
            <Skeleton height="32px" width="100px" borderRadius="md" />
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <BankCardSkeleton />
            <BankCardSkeleton />
          </SimpleGrid>
        </CardBody>
      </Card>
    );
  }

  // Show empty state if no tenant selected
  if (!selectedTenant) {
    return null;
  }

  return (
    <>
      {/* Reconnect Plaid Link (renders nothing, opens modal) */}
      {reconnectingTokenId && (
        <ReconnectPlaidLink
          tokenId={reconnectingTokenId}
          onSuccess={handleReconnectSuccess}
          onExit={handleReconnectExit}
        />
      )}

      <Card bg="bg.surface" borderColor="xacto.mutedNavy" borderWidth="1px">
        <CardHeader pb={2}>
          <HStack justify="space-between" align="center">
            <HStack spacing={2}>
              <Heading size="sm" color="text.primary">
                Connected Banks
              </Heading>
              {plaidTokens.length > 0 && (
                <Badge colorScheme="cyan" borderRadius="full">
                  {plaidTokens.length}
                </Badge>
              )}
            </HStack>
            <AddBankButton onSuccess={handleAddSuccess} />
          </HStack>
        </CardHeader>

      <CardBody pt={2} maxH="400px" overflowY="auto">
        {plaidTokens.length === 0 ? (
          <Alert
            status="info"
            variant="subtle"
            bg="bg.muted"
            borderRadius="md"
          >
            <AlertIcon color="accent.primary" />
            <Box>
              <AlertTitle color="text.primary" fontSize="sm">
                No Banks Connected
              </AlertTitle>
              <AlertDescription color="text.secondary" fontSize="xs">
                Connect a bank account to access transaction data and account information.
              </AlertDescription>
            </Box>
          </Alert>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            {plaidTokens.map((token) => (
              <BankCard
                key={token.id}
                token={token}
                onTestResult={onTestResult}
                onDisconnect={handleDisconnect}
                onReconnect={handleReconnect}
              />
            ))}
          </SimpleGrid>
        )}
      </CardBody>
    </Card>
    </>
  );
};

export default ConnectedBanksList;
